import React, { useState } from "react";
import styles from "./DraftEnquiries.module.css";
import DraftRow from "./DraftRow";
import DraftDetail from "./DraftDetail";
import Pagination from "react-js-pagination";
import useDraftEnquiries from "./hooks/useDraftEnquiries";
import ConfirmDialog from "../../Shared/ConfirmDialog2/ConfirmDialog";

const ITEMS_PER_PAGE = 10;

const DraftEnquiries = () => {
  const {
    drafts,
    totalCount,
    truckTypes,
    materialCategories,
    editingDraft,
    page,
    isLoading,
    selectedIds,
    invalidIds,
    isAllSelected,
    isIndeterminate,
    handlePageChange,
    handleFormChange,
    handleEdit,
    handleBack,
    handleSave,
    handleAccept,
    handleReject,
    handleToggleSelect,
    handleSelectAll,
    handleBulkReject,
    handleBulkAccept,
  } = useDraftEnquiries();

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    variant: "default",
  });

  const closeConfirm = () => setConfirmDialog((d) => ({ ...d, isOpen: false }));

  const e = (draft, isBulk = false) =>
    setConfirmDialog({
      isOpen: true,
      title: isBulk ? "Accept Selected" : "Accept Enquiry",
      message: isBulk
        ? `Accept ${selectedIds.size} selected draft(s) and submit as spot enquiries?`
        : "Accept this draft and submit it as a spot enquiry?",
      onConfirm: () => {
        closeConfirm();
        isBulk ? handleBulkAccept() : handleAccept(draft);
      },
      variant: "default",
    });

  const handleAcceptWithConfirm = (draftId, isBulk = false) =>
    setConfirmDialog({
      isOpen: true,
      title: isBulk ? "Accept Selected" : "Accept Enquiry",
      message: isBulk
        ? `Accept ${selectedIds.size} selected draft(s) and submit as spot enquiries?`
        : "Accept this draft and submit it as a spot enquiry?",
      onConfirm: () => {
        closeConfirm();
        isBulk ? handleBulkAccept() : handleAccept(draftId);
      },
      variant: "default",
    });

  const handleRejectWithConfirm = (draftId, isBulk = false) =>
    setConfirmDialog({
      isOpen: true,
      title: isBulk ? "Reject Selected" : "Reject Enquiry",
      message: isBulk
        ? `Reject ${selectedIds.size} selected draft(s)? This cannot be undone.`
        : "Reject this draft? This cannot be undone.",
      onConfirm: () => {
        closeConfirm();
        isBulk ? handleBulkReject() : handleReject(draftId);
      },
      variant: "danger",
    });

  return (
    <>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
      />

      {!editingDraft ? (
        <div className={styles.container}>
          <div className={styles.header}>
            <div>
              <h3 className={styles.pageTitle}>Draft Enquiries</h3>
              <div className={styles.agentNote}>
                <i
                  className="fa fa-magic"
                  style={{ marginRight: "5px", color: "#8b5cf6" }}
                ></i>
                Drafts are auto-created by the <strong>LoRRI Spot Agent</strong>{" "}
                from incoming emails. Accept to publish as a live enquiry, or
                reject to discard.
              </div>

              {drafts && drafts.length > 0 && (
                <div className={styles.selectionToolbar}>
                  <button
                    className={styles.btnSelectAll}
                    onClick={handleSelectAll}
                  >
                    <i
                      className={`fa ${isAllSelected ? "fa-check-square-o" : isIndeterminate ? "fa-minus-square-o" : "fa-square-o"}`}
                    ></i>
                    {isAllSelected ? "Unselect All" : "Select All"}
                  </button>

                  <span className={styles.toolbarDivider} />

                  <button
                    className={`${styles.btnBulkAccept} ${selectedIds.size === 0 ? styles.btnBulkDisabled : ""}`}
                    disabled={selectedIds.size === 0}
                    onClick={() => handleAcceptWithConfirm(null, true)}
                  >
                    <i className="fa fa-check"></i>
                    Accept{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                  </button>

                  <button
                    className={`${styles.btnBulkReject} ${selectedIds.size === 0 ? styles.btnBulkDisabled : ""}`}
                    disabled={selectedIds.size === 0}
                    onClick={() => handleRejectWithConfirm(null, true)}
                  >
                    <i className="fa fa-times"></i>
                    Reject{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                  </button>
                </div>
              )}
            </div>
            {totalCount > 0 && (
              <span className={styles.countBadge}>
                {totalCount} draft{totalCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {isLoading && drafts.length === 0 ? (
            <div className={styles.loadingWrap}>
              <i className="fa fa-spinner fa-spin" />
            </div>
          ) : drafts.length === 0 ? (
            <div className={styles.emptyState}>
              <i
                className="fa fa-inbox"
                style={{
                  fontSize: "36px",
                  color: "#cbd5e1",
                  marginBottom: "12px",
                }}
              ></i>
              <p>No draft enquiries at the moment.</p>
              <p style={{ fontSize: "12px", color: "#94a3b8" }}>
                AI-generated drafts from your emails will appear here.
              </p>
            </div>
          ) : (
            <div className={styles.draftList}>
              {drafts.map((draft) => (
                <DraftRow
                  key={draft.draft_id}
                  draft={draft}
                  isSelected={selectedIds.has(draft.draft_id)}
                  isInvalid={invalidIds.has(draft.draft_id)}
                  onToggleSelect={handleToggleSelect}
                  onEdit={handleEdit}
                  onReject={handleRejectWithConfirm}
                  onAccept={handleAcceptWithConfirm}
                />
              ))}
            </div>
          )}

          {totalCount > ITEMS_PER_PAGE && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <Pagination
                activePage={page + 1}
                itemsCountPerPage={ITEMS_PER_PAGE}
                totalItemsCount={totalCount}
                onChange={handlePageChange}
                itemClass="page-item"
                linkClass="page-link"
                hideDisabled
              />
            </div>
          )}
        </div>
      ) : (
        <div className={styles.container}>
          <DraftDetail
            draft={editingDraft}
            formData={editingDraft.formData}
            onChange={handleFormChange}
            onBack={handleBack}
            onSave={handleSave}
            onAccept={handleAcceptWithConfirm}
            onReject={handleRejectWithConfirm}
            truckOptions={truckTypes}
            productOptions={materialCategories}
          />
        </div>
      )}
    </>
  );
};

export default DraftEnquiries;
